import { createContext, useContext, useEffect, useState } from 'react';
import { api } from '../../services/api.js';
import { useMisc } from '../../contexts/MiscContext.js';
import { toast } from 'react-toastify';
import CookiesService from '../../services/cookies';
import RevisaoMateriaPrimaMain from '../../pages/MP/RevisaoMateriaPrima.jsx';

const RevisaoMateriaPrimaContext = createContext();
export const useRevisaoMateriaPrima = () => useContext(RevisaoMateriaPrimaContext);
const userLogin = CookiesService.getUserLogin();

const getInitialFilterState = () => {
    const defaultState = {
        pedido: '',
        material: '',
        corMaterial: '',
        statusRevisao: ''
    };

    try {
        const userSupplierPitagoras = CookiesService.getUserSupplierPitagoras();
        
        if (userSupplierPitagoras && userSupplierPitagoras.pedido) {
            const { pedido, ...restOfCookieData } = userSupplierPitagoras;
            CookiesService.setUserSupplierPitagoras(restOfCookieData);
            return { ...defaultState, pedido: pedido };
        }
    } catch (e) {
        console.error("Erro ao ler cookie no estado inicial:", e);
    }

    return defaultState;
};


export const RevisaoMateriaPrimaProvider = () => {
    const { setShowLoading } = useMisc();
    const [showCad, setShowCad] = useState(false);
    const [mainData, setMainData] = useState([]);
    const [originalData, setOriginalData] = useState([]);
    const [isRevisorView, setIsRevisorView] = useState(false);
    const [filterObj, setFilterObj] = useState(getInitialFilterState);
    const [statusOptions, setStatusOptions] = useState([]);


    const fetchStatusOptions = async () => {
        try {
            const response = await api.get('/mp/triangulacao-revisao/pedidos/status');
            const data = Array.isArray(response.data) ? response.data : response.data.rows;
            if (Array.isArray(data)) {
                setStatusOptions(
                    data.map(s => ({ value: s.descricaoStatus, label: s.descricaoStatus }))
                );
            }
        } catch (error) {
            toast.error('Erro ao buscar status do filtro');
        }
    };


    useEffect(() => {
        if (!filterObj.pedido && !filterObj.material && !filterObj.corMaterial && !filterObj.statusRevisao) {
            setMainData(originalData);
        } else {
            handleFilterButtonClick();
        }
    }, [filterObj, originalData]);

    const handleSetFilterObj = (key, value) => {
        setFilterObj(x => ({ ...x, [key]: value }));
    };

    const handleFilterMainGrid = async () => {
        try {
            setShowLoading(true);
            const userSupplierPitagoras = CookiesService.getUserSupplierPitagoras();
            const internalUser = CookiesService.getUserLogin();

            const headers = {}
            if (userSupplierPitagoras?.fornecedor) {
                headers['Pitagoras-Data'] = userSupplierPitagoras?.fornecedor
            } else {
                headers['User-Data'] = internalUser
            }

            const response = await api.get('/mp/triangulacao-revisao/pedidos', {
                params: { ...filterObj },
                headers: headers
            });

            const data = Array.isArray(response.data) ? response.data : response.data.rows;
            setMainData(data);
            setOriginalData(data);
        } catch (error) {
            toast.error('Erro ao filtrar dados de revisão de matéria prima');
        } finally {
            setShowLoading(false);
        }
    };

    useEffect(() => {
        handleFilterMainGrid();
        fetchStatusOptions();
    }, [setShowLoading]);

    const handleFilterButtonClick = () => {
        if (!filterObj.pedido && !filterObj.material && !filterObj.corMaterial && !filterObj.statusRevisao) {
            setMainData(originalData);
            return;
        }
        setMainData(originalData.filter(row => {
            const pedidoMatch = !filterObj.pedido || String(row.pedido).includes(String(filterObj.pedido));
            const materialMatch = !filterObj.material || String(row.material).includes(String(filterObj.material));
            const corMatch = !filterObj.corMaterial || String(row.corMaterial).includes(String(filterObj.corMaterial));
            const statusMatch = !filterObj.statusRevisao || String(row.statusRevisao) === String(filterObj.statusRevisao);
            return pedidoMatch && materialMatch && corMatch && statusMatch;
        }));
    };

    const marcarAuditoria = async ({ idMpTriangulacaoRev, idUsuario, dataAuditoria }) => {
        try {
            setShowLoading(true);
            await api.patch('/mp/triangulacao-revisao/pedidos/marcar-auditoria', {
                idMpTriangulacaoRev,
                idUsuario,
                dataAuditoria
            });
            toast.success('Auditoria marcada com sucesso!');
            await handleFilterMainGrid();
        } catch (error) {
            toast.error('Erro ao marcar auditoria');
        } finally {
            setShowLoading(false);
        }
    };

    const desmarcarAuditoria = async (idMpTriangulacaoRev) => {
        try {
            setShowLoading(true);
            await api.patch('/mp/triangulacao-revisao/pedidos/desmarcar-auditoria', {
                idMpTriangulacaoRev
            });
            toast.success('Auditoria desmarcada com sucesso!');
            await handleFilterMainGrid();
        } catch (error) {
            toast.error('Erro ao desmarcar auditoria');
        } finally {
            setShowLoading(false);
        }
    };

    const continuarLotesPecas = async (dadosLotes) => {
        try {
            setShowLoading(true);
            await api.post('/mp/triangulacao-revisao/pedidos/lotes/pecas', dadosLotes);
            const totalPecas = dadosLotes.reduce((soma, item) => soma + item.qtdePecas, 0)
            setMainData(prevData => prevData.map(item => item.idMpTriangulacaoRev === dadosLotes[0].idMpTriangulacaoRev ? { ...item, qtdePecas: totalPecas } : item));
            toast.success('Lotes cadastrados com sucesso!');
        } catch (error) {
            toast.error('Erro ao cadastrar lotes');
        } finally {
            setShowLoading(false);
        }
    };

    const buscarLotesNumericosPorId = async (idMpTriangulacaoRev) => {
        try {
            setShowLoading(true);
            const response = await api.get(`/mp/triangulacao-revisao/pedidos/lotes/pecas/id/${idMpTriangulacaoRev}`);
            const data = Array.isArray(response.data) ? response.data : response.data.rows;
            return data.map((item, idx) => item.lote ? item.lote : idx + 1);
        } catch (error) {
            toast.error('Erro ao buscar lotes');
            return [];
        } finally {
            setShowLoading(false);
        }
    };

    const patchLotesPecas = async (dadosLotes) => {
        try {
            setShowLoading(true);
            await api.patch('/mp/triangulacao-revisao/pedidos/lotes/pecas', dadosLotes);
            toast.success('Lotes atualizados com sucesso!');
        } catch (error) {
            toast.error('Erro ao atualizar lotes');
        } finally {
            setShowLoading(false);
        }
    };

    // Função para registrar análise de peça (POST /pedidos/analise/peca)
    const registrarAnalisePeca = async (dadosAnalise, isMultipart = false) => {
        try {
            setShowLoading(true);
            let response;
            if (isMultipart) {
                response = await api.post('/mp/triangulacao-revisao/pedidos/analise/peca', dadosAnalise, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
            } else {
                response = await api.post('/mp/triangulacao-revisao/pedidos/analise/peca', dadosAnalise);
            }
            toast.success('Análise registrada com sucesso!');
            if (response && response.data) {
                if (Array.isArray(response.data)) {
                    return response.data[0];
                } else if (response.data.row) {
                    return response.data.row;
                } else {
                    return response.data;
                }
            }
            return null;
        } catch (error) {
            toast.error('Erro ao registrar análise da peça');
            return null;
        } finally {
            setShowLoading(false);
        }
    };

    // Busca a lista de análises de peças para o pedido/lote
    const fetchAnalisePecas = async (idMpTriangulacaoRev) => {
        try {
            setShowLoading(true);
            const response = await api.get(`/mp/triangulacao-revisao/pedidos/analise/peca/id/${idMpTriangulacaoRev}`);
            const data = Array.isArray(response.data) ? response.data : (response.data.rows ? response.data.rows : []);
            return data;
        } catch (error) {
            toast.error('Erro ao buscar análises das peças');
            return [];
        } finally {
            setShowLoading(false);
        }
    };

    // Busca a lista de defeitos de peças para o pedido/lote
    const fetchDefeitosPecas = async (idMpTriangulacaoRev) => {
        try {
            setShowLoading(true);
            const response = await api.get(`/mp/triangulacao-revisao/pedidos/analise/peca/defeitos/id/${idMpTriangulacaoRev}`);
            const data = Array.isArray(response.data) ? response.data : (response.data.rows ? response.data.rows : []);
            return data;
        } catch (error) {
            toast.error('Erro ao buscar defeitos das peças');
            return [];
        } finally {
            setShowLoading(false);
        }
    };


    // Deletar defeito de peça de análise
    const deletarAnalisePecaDefeito = async (idMpTriangulacaoRevAnalisePeca) => {
        try {
            setShowLoading(true);
            await api.delete(`/mp/triangulacao-revisao/pedidos/analise/peca/id/${idMpTriangulacaoRevAnalisePeca}`);
            toast.success('Análise da peça removida com sucesso!');
            return true;
        } catch (error) {
            toast.error('Erro ao remover análise da peça');
            return false;
        } finally {
            setShowLoading(false);
        }
    };

    // Deletar defeito individual de análise de peça
    const deletarDefeitoAnalisePeca = async (idMpTriangulacaoRevDefeitosPeca, idMpTriangulacaoRevAnalisePeca) => {
        try {
            setShowLoading(true);
            await api.delete(`/mp/triangulacao-revisao/pedidos/analise-peca/${idMpTriangulacaoRevAnalisePeca}/defeito/${idMpTriangulacaoRevDefeitosPeca}`);
            toast.success('Defeito removido com sucesso!');
            return true;
        } catch (error) {
            toast.error('Erro ao remover defeito');
            return false;
        } finally {
            setShowLoading(false);
        }
    };

    // Buscar opções de defeito
    const fetchOpcoesDefeito = async () => {
        try {
            setShowLoading(true);
            const response = await api.get('/mp/triangulacao-revisao/pedidos/defeitos/tipos-defeitos');
            const data = Array.isArray(response.data) ? response.data : (response.data.rows ? response.data.rows : []);
            return data;
        } catch (error) {
            toast.error('Erro ao buscar opções de defeito');
            return [];
        } finally {
            setShowLoading(false);
        }
    };

    // Buscar opções de localização
    const fetchOpcoesLocalizacao = async () => {
        try {
            setShowLoading(true);
            const response = await api.get('/mp/triangulacao-revisao/pedidos/defeitos/localizacoes-defeitos');
            const data = Array.isArray(response.data) ? response.data : (response.data.rows ? response.data.rows : []);
            return data;
        } catch (error) {
            toast.error('Erro ao buscar opções de localização');
            return [];
        } finally {
            setShowLoading(false);
        }
    };

    // Buscar opções de tamanho
    const fetchOpcoesTamanho = async () => {
        try {
            setShowLoading(true);
            const response = await api.get('/mp/triangulacao-revisao/pedidos/defeitos/tamanhos-defeitos');
            const data = Array.isArray(response.data) ? response.data : (response.data.rows ? response.data.rows : []);
            return data;
        } catch (error) {
            toast.error('Erro ao buscar opções de tamanho');
            return [];
        } finally {
            setShowLoading(false);
        }
    };

    // Registrar análise de peça com defeitos (POST /mp/triangulacao-revisao/pedidos/analise/peca/defeitos)
    const registrarAnalisePecaComDefeitos = async (payload) => {
        try {
            setShowLoading(true);
            const response = await api.post('/mp/triangulacao-revisao/pedidos/analise/peca/defeitos', payload);
            toast.success('Análise de peça e defeitos registrada com sucesso!');
            return response.data;
        } catch (error) {
            toast.error('Erro ao registrar análise de peça com defeitos');
            return null;
        } finally {
            setShowLoading(false);
        }
    };

    // Editar tipo de tecido
    const editarTipoTecido = async ({ idMpTriangulacaoRev, tipoTecido }) => {
        try {
            setShowLoading(true);
            await api.patch('/mp/triangulacao-revisao/pedidos/tipo-tecido', {
                idMpTriangulacaoRev,
                tipoTecido
            });
            toast.success('Tipo de tecido atualizado com sucesso!');
            await handleFilterMainGrid();
        } catch (error) {
            toast.error('Erro ao atualizar tipo de tecido');
        } finally {
            setShowLoading(false);
        }
    };

    // Buscar tipo/modal de tonalidade para peça/lote
    const getModalTonalidadeTipo = async (idMpTriangulacaoRev, lote) => {
        try {
            setShowLoading(true);
            const response = await api.get(`/mp/triangulacao-revisao/pedidos/modal-tonalidade/id/${idMpTriangulacaoRev}/lote/${lote}`);
            // O backend retorna { modal: 1 } ou { modal: 2 } ou { modal: null }
            return response.data?.modal ?? null;
        } catch (error) {
            toast.error('Erro ao buscar tipo/modal de tonalidade');
            return null;
        } finally {
            setShowLoading(false);
        }
    };

    // Buscar índices para o modal tonalidade
    const fetchIndicesModalTonalidade = async (idMpTriangulacaoRev, lote) => {
        try {
            setShowLoading(true);
            // Substitua a URL abaixo pelo endpoint correto do backend
            const response = await api.get('/mp/triangulacao-revisao/pedidos/tonalidade/indices');
            // Supondo que o backend retorna um array de índices
            return Array.isArray(response.data) ? response.data : (response.data.rows ? response.data.rows : []);
        } catch (error) {
            toast.error('Erro ao buscar índices do modal tonalidade');
            return [];
        } finally {
            setShowLoading(false);
        }
    };

    // Editar nome da peça (PATCH /pedidos/analise/peca/nome-peca/)
    const patchNomePeca = async ({ idMpTriangulacaoRevAnalisePeca, pecaCustomizada }) => {
        try {
            setShowLoading(true);
            await api.patch('/mp/triangulacao-revisao/pedidos/analise/peca/nome-peca/', {
                idMpTriangulacaoRevAnalisePeca,
                pecaCustomizada
            });
            toast.success('Nome da peça atualizado com sucesso!');
        } catch (error) {
            toast.error('Erro ao atualizar nome da peça');
        } finally {
            setShowLoading(false);
        }
    };

    // Finalizar revisão do pedido
    const finalizarRevisaoPedido = async (idMpTriangulacaoRev) => {
        try {
            setShowLoading(true);

            const userSupplierPitagoras = CookiesService.getUserSupplierPitagoras();
            const internalUser = CookiesService.getUserLogin();

            const headers = {}
            if (userSupplierPitagoras?.fornecedor) {
                headers['Pitagoras-Data'] = userSupplierPitagoras?.fornecedor
            } else {
                headers['User-Data'] = internalUser
            }

            await api.post(`/mp/triangulacao-revisao/pedidos/finalizar/id/${idMpTriangulacaoRev}`, {}, { headers: headers });
            toast.success('Revisão finalizada com sucesso!');
            await handleFilterMainGrid();
        } catch (error) {
            toast.error('Erro ao finalizar revisão do pedido');
        } finally {
            setShowLoading(false);
        }
    };

    // Buscar status atual do pedido (Aprovado/Reprovado)
    const fetchStatusAtualPedido = async (idMpTriangulacaoRev) => {
        try {
            setShowLoading(true);
            const response = await api.get(`/mp/triangulacao-revisao/pedidos/${idMpTriangulacaoRev}/status`);
            // Retorna o objeto completo para flexibilidade de uso
            return response.data;
        } catch (error) {
            toast.error('Erro ao buscar status atual do pedido');
            return null;
        } finally {
            setShowLoading(false);
        }
    };

    return (
        <RevisaoMateriaPrimaContext.Provider value={{
            userLogin,
            setShowLoading,
            showCad,
            setShowCad,
            mainData,
            setMainData,
            filterObj,
            setFilterObj,
            handleSetFilterObj,
            handleFilterButtonClick,
            handleFilterMainGrid,
            originalData,
            setOriginalData,
            isRevisorView,
            setIsRevisorView,
            marcarAuditoria,
            desmarcarAuditoria,
            statusOptions,
            continuarLotesPecas,
            buscarLotesNumericosPorId,
            patchLotesPecas,
            registrarAnalisePeca,
            fetchAnalisePecas,
            fetchDefeitosPecas,
            fetchOpcoesDefeito,
            fetchOpcoesLocalizacao,
            fetchOpcoesTamanho,
            deletarAnalisePecaDefeito,
            deletarDefeitoAnalisePeca,
            registrarAnalisePecaComDefeitos,
            editarTipoTecido,
            getModalTonalidadeTipo,
            fetchIndicesModalTonalidade,
            finalizarRevisaoPedido,
            patchNomePeca,
            fetchStatusAtualPedido
        }}>
            <RevisaoMateriaPrimaMain />
        </RevisaoMateriaPrimaContext.Provider>
    );
}